/**
 * User Context - данные пользователя и баланс
 */

import React, {
    createContext,
    useState,
    useEffect,
    useCallback,
    useContext,
} from "react";
import { useAuthContext } from "../AuthContext/AuthProvider";
import { userService } from "../../api/services/userService";

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
    const { isAuthenticated, user } = useAuthContext();

    const [profile, setProfile] = useState(null);
    const [donateBalance, setDonateBalance] = useState(0);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Загрузить баланс доната
     */
    const fetchDonateBalance = useCallback(async () => {
        if (!isAuthenticated) return;

        setLoading(true);
        try {
            const data = await userService.getDonateBalance();
            setDonateBalance(data || 0);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated]);

    /**
     * Проверить админку
     */
    const checkAdmin = useCallback(async () => {
        if (!isAuthenticated) return;

        try {
            const data = await userService.checkAdmin();
            setIsAdmin(data?.is_admin || data > 0 || false);
        } catch (err) {
            setIsAdmin(false);
        }
    }, [isAuthenticated]);

    /**
     * Обновить настройки
     */
    const updateSettings = useCallback(async (settings) => {
        setLoading(true);
        try {
            await userService.updateSettings(settings);
            setProfile((prev) => ({ ...prev, ...settings }));
            return { success: true };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Изменить пароль
     */
    const changePassword = useCallback(async (oldPassword, newPassword) => {
        setLoading(true);
        try {
            await userService.changePassword(oldPassword, newPassword);
            return { success: true };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    }, []);

    // Загрузка данных при авторизации
    useEffect(() => {
        if (isAuthenticated) {
            fetchDonateBalance();
            checkAdmin();
        } else {
            setDonateBalance(0);
            setIsAdmin(false);
            setProfile(null);
        }
    }, [isAuthenticated, fetchDonateBalance, checkAdmin]);

    const value = {
        profile,
        donateBalance,
        isAdmin,
        loading,
        error,
        fetchDonateBalance,
        updateSettings,
        changePassword,
    };

    return (
        <UserContext.Provider value={value}>{children}</UserContext.Provider>
    );
};

/**
 * Hook для использования User Context
 */
export const useUserContext = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error("useUserContext must be used within UserProvider");
    }
    return context;
};

export default UserProvider;
